import { Link } from '@tanstack/react-router';
import { XIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import type { RelationRowModel } from '../types';

/**
 * One connected task, however it was reached.
 *
 * There used to be two of these, one per surface, showing different fields: the
 * dialog's row showed a step name the editor's did not, and the editor's offered
 * a remove control the dialog's could not. Reading them side by side, a person
 * could not tell they were the same kind of thing. One row, fed by one view
 * model, is what makes the two groups in the Relations tab read as one feature.
 *
 * Everything conditional here is conditional on the *data*, not on which caller
 * built it: a remove control appears when a handler exists, distance appears when
 * the task was reached through another, and the title is a link when the task can
 * be opened at all.
 */
export function RelationRow({ row }: { row: RelationRowModel }) {
  return (
    <li className="border-border flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2.5 py-1.5 text-sm">
      {row.link ? (
        <Link
          {...row.link}
          className="text-foreground focus-visible:ring-ring min-w-0 flex-1 truncate text-left hover:underline focus-visible:ring-2 focus-visible:outline-none"
        >
          {row.title}
        </Link>
      ) : (
        /*
          Not a disabled control: a disabled thing implies a state that could
          change. This task's workflow is gone from the workspace, so there is no
          address to go to, and saying so is the honest rendering (FR-028).
        */
        <span className="min-w-0 flex-1 truncate">
          <span className="text-foreground">{row.title}</span>{' '}
          <span className="text-muted-foreground text-xs">
            cannot be opened from here
          </span>
        </span>
      )}

      {row.context ? (
        <span className="text-muted-foreground shrink-0 text-xs">
          {row.context}
        </span>
      ) : null}

      <Badge variant="outline" className="shrink-0">
        {row.label}
      </Badge>

      {/* Only a task reached through another has these, and the pair is what
          explains why its row offers no remove control (FR-018, FR-019). */}
      {row.distance !== null ? (
        <span className="text-muted-foreground shrink-0 text-xs">
          {row.distance} away
          {row.viaTitle ? `, through ${row.viaTitle}` : ''}
        </span>
      ) : null}

      {row.onRemove ? (
        <button
          type="button"
          aria-label={`Remove relation to ${row.title}`}
          onClick={row.onRemove}
          className="hover:bg-accent focus-visible:ring-ring shrink-0 rounded-md p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <XIcon
            aria-hidden="true"
            className="text-muted-foreground size-3.5"
            strokeWidth={1.75}
          />
        </button>
      ) : null}
    </li>
  );
}
